import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { 
  ChevronRight, 
  ChevronLeft,
  Dumbbell,
  Target,
  Flame,
  Zap,
  Heart,
  Check,
  X
} from 'lucide-react';

const OnboardingModal = ({ onComplete, onSkip }) => {
  const [step, setStep] = useState(1);
  const [data, setData] = useState({
    machine_type: null,
    experience_level: 'beginner',
    fitness_goals: [],
    classes_per_week_goal: 3
  });

  const machines = [
    { id: 'micro', name: 'Lagree Micro', desc: 'Compact home machine' },
    { id: 'mini_pro', name: 'Lagree Mini Pro', desc: 'Professional home machine' },
    { id: 'megaformer', name: 'Studio Megaformer', desc: 'Full-size studio machine' },
    { id: 'none', name: "I don't have one yet", desc: 'Planning to purchase' }
  ];

  const experienceLevels = [
    { id: 'beginner', name: 'New to Lagree', desc: "Just getting started", icon: '🌱' },
    { id: 'intermediate', name: 'Some Studio Experience', desc: "I've taken studio classes", icon: '💪' },
    { id: 'advanced', name: 'Advanced Practitioner', desc: 'I practice regularly', icon: '🔥' }
  ];

  const fitnessGoals = [
    { id: 'strength', name: 'Build Strength', icon: Dumbbell },
    { id: 'toning', name: 'Tone & Sculpt', icon: Target },
    { id: 'weight_loss', name: 'Lose Weight', icon: Flame },
    { id: 'flexibility', name: 'Improve Flexibility', icon: Zap },
    { id: 'endurance', name: 'Build Endurance', icon: Heart },
    { id: 'rehabilitation', name: 'Rehabilitation', icon: Heart }
  ];

  const toggleGoal = (goalId) => {
    if (data.fitness_goals.includes(goalId)) {
      setData({ ...data, fitness_goals: data.fitness_goals.filter(g => g !== goalId) });
    } else {
      setData({ ...data, fitness_goals: [...data.fitness_goals, goalId] });
    }
  };

  const handleComplete = () => {
    onComplete(data);
  };

  const canProceed = () => {
    switch (step) {
      case 1: return data.machine_type !== null;
      case 2: return data.experience_level !== null;
      case 3: return data.fitness_goals.length > 0;
      case 4: return data.classes_per_week_goal > 0;
      default: return true;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4">
      <div className="bg-[#121416] border border-zinc-800 rounded-sm w-full max-w-lg overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-zinc-800 flex items-center justify-between">
          <div>
            <p className="text-sm text-teal-400 font-medium">Step {step} of 4</p>
            <h2 className="text-xl font-bold">Welcome to Lagree MVMT</h2>
          </div>
          <button onClick={onSkip} className="text-zinc-500 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Progress Bar */}
        <div className="h-1 bg-zinc-800">
          <div 
            className="h-full bg-teal-400 transition-all duration-300"
            style={{ width: `${(step / 4) * 100}%` }}
          />
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Step 1: Machine Type */}
          {step === 1 && (
            <div>
              <h3 className="text-lg font-bold mb-2">Which Lagree machine do you have?</h3>
              <p className="text-zinc-500 text-sm mb-6">This helps us recommend the right classes for your setup.</p>
              <div className="space-y-3">
                {machines.map((machine) => (
                  <button
                    key={machine.id}
                    onClick={() => setData({ ...data, machine_type: machine.id })}
                    className={`w-full p-4 rounded-sm border text-left transition-colors ${
                      data.machine_type === machine.id
                        ? 'border-teal-400 bg-teal-400/10'
                        : 'border-zinc-700 hover:border-zinc-600'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{machine.name}</p>
                        <p className="text-sm text-zinc-500">{machine.desc}</p>
                      </div>
                      {data.machine_type === machine.id && (
                        <Check className="w-5 h-5 text-teal-400" />
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: Experience Level */}
          {step === 2 && (
            <div>
              <h3 className="text-lg font-bold mb-2">What's your experience level?</h3>
              <p className="text-zinc-500 text-sm mb-6">We'll personalize your recommendations based on this.</p>
              <div className="space-y-3">
                {experienceLevels.map((level) => (
                  <button
                    key={level.id}
                    onClick={() => setData({ ...data, experience_level: level.id })}
                    className={`w-full p-4 rounded-sm border text-left transition-colors ${
                      data.experience_level === level.id
                        ? 'border-teal-400 bg-teal-400/10'
                        : 'border-zinc-700 hover:border-zinc-600'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{level.icon}</span>
                        <div>
                          <p className="font-medium">{level.name}</p>
                          <p className="text-sm text-zinc-500">{level.desc}</p>
                        </div>
                      </div>
                      {data.experience_level === level.id && (
                        <Check className="w-5 h-5 text-teal-400" />
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 3: Fitness Goals */}
          {step === 3 && (
            <div>
              <h3 className="text-lg font-bold mb-2">What are your fitness goals?</h3>
              <p className="text-zinc-500 text-sm mb-6">Select all that apply. We'll suggest classes that match.</p>
              <div className="grid grid-cols-2 gap-3">
                {fitnessGoals.map((goal) => (
                  <button
                    key={goal.id}
                    onClick={() => toggleGoal(goal.id)}
                    className={`p-4 rounded-sm border text-center transition-colors ${
                      data.fitness_goals.includes(goal.id)
                        ? 'border-teal-400 bg-teal-400/10'
                        : 'border-zinc-700 hover:border-zinc-600'
                    }`}
                  >
                    <goal.icon className={`w-6 h-6 mx-auto mb-2 ${
                      data.fitness_goals.includes(goal.id) ? 'text-teal-400' : 'text-zinc-400'
                    }`} />
                    <p className="text-sm font-medium">{goal.name}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 4: Weekly Goal */}
          {step === 4 && (
            <div>
              <h3 className="text-lg font-bold mb-2">How many classes per week?</h3>
              <p className="text-zinc-500 text-sm mb-6">Set a realistic weekly goal to stay consistent.</p>
              <div className="flex items-center justify-center gap-4 py-8">
                <button
                  onClick={() => setData({ ...data, classes_per_week_goal: Math.max(1, data.classes_per_week_goal - 1) })}
                  className="w-12 h-12 rounded-full border border-zinc-700 flex items-center justify-center hover:border-teal-400 transition-colors"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
                <div className="text-center">
                  <span className="text-6xl font-bold text-teal-400">{data.classes_per_week_goal}</span>
                  <p className="text-zinc-500 mt-2">classes per week</p>
                </div>
                <button
                  onClick={() => setData({ ...data, classes_per_week_goal: Math.min(7, data.classes_per_week_goal + 1) })}
                  className="w-12 h-12 rounded-full border border-zinc-700 flex items-center justify-center hover:border-teal-400 transition-colors"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
              </div>
              <p className="text-center text-sm text-zinc-500">
                {data.classes_per_week_goal <= 2 && "Great for beginners! Consistency is key."}
                {data.classes_per_week_goal >= 3 && data.classes_per_week_goal <= 4 && "Perfect balance for steady progress!"}
                {data.classes_per_week_goal >= 5 && "Ambitious! You'll see results fast."}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-zinc-800 flex justify-between">
          {step > 1 ? (
            <Button variant="ghost" onClick={() => setStep(step - 1)}>
              <ChevronLeft className="w-4 h-4 mr-2" /> Back
            </Button>
          ) : (
            <Button variant="ghost" onClick={onSkip} className="text-zinc-500">
              Skip for now
            </Button>
          )}
          
          {step < 4 ? (
            <Button 
              onClick={() => setStep(step + 1)}
              disabled={!canProceed()}
              className="bg-teal-400 text-black font-bold hover:bg-teal-300 disabled:opacity-50"
            >
              Continue <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button 
              onClick={handleComplete}
              disabled={!canProceed()}
              className="bg-teal-400 text-black font-bold hover:bg-teal-300 disabled:opacity-50"
            >
              Get Started <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default OnboardingModal;
